import { useState, useMemo } from 'react';
import {
  Box,
  Center,
  Stack,
  Spinner
} from '@chakra-ui/react';
import { ItemDataSuggestionModel, ArtistModel } from 'models';
import { ProcessStatus } from 'models/types';
import ItemDataSuggestionCard from '../../Item/ItemDataSuggestionCard/ItemDataSuggestionCard';
import ItemDataSuggestionsFiltersForm from '../../Form/ItemDataSuggestionsFiltersForm/ItemDataSuggestionsFiltersForm';
import EmptyState from '../../EmptyState/EmptyState';
import PaginationFooter from '../../Pagination/PaginationFooter/PaginationFooter';
import { filterItemsByQuery } from 'utils/search';

const PER_PAGE = 10;

export interface DetailedItemDataSuggestionGridProps {
  suggestions: ItemDataSuggestionModel[];
  isLoading: boolean;
  onApprove?: (suggestionId: string) => void;
  onReject?: (suggestionId: string) => void;
  defaultStatus?: ProcessStatus;
  onStatusChange?: (status: ProcessStatus) => void;
  isActionLoading?: boolean;
  availableArtists?: ArtistModel[];
}

export default function DetailedItemDataSuggestionGrid({
  suggestions,
  isLoading,
  onApprove,
  onReject,
  defaultStatus = ProcessStatus.PENDING,
  onStatusChange,
  isActionLoading = false,
  availableArtists = []
}: DetailedItemDataSuggestionGridProps) {
  const [selectedStatus, setSelectedStatus] = useState<ProcessStatus>(defaultStatus);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedArtists, setSelectedArtists] = useState<ArtistModel[]>([]);
  const [page, setPage] = useState(1);

  const filteredSuggestions = useMemo(() => {
    let filtered = suggestions;

    if (selectedArtists.length > 0) {
      const artistIds = new Set(selectedArtists.map(artist => artist.id));
      filtered = filtered.filter(suggestion => artistIds.has(suggestion.item.artistId));
    }

    if (searchQuery?.trim()) {
      const items = filtered.map(s => s.item);
      const filteredItems = filterItemsByQuery(items, searchQuery);
      const allowedIds = new Set(filteredItems.map(item => item.id));
      filtered = filtered.filter(s => allowedIds.has(s.item.id));
    }

    return filtered;
  }, [suggestions, searchQuery, selectedArtists]);

  const paginatedSuggestions = useMemo(() => {
    const startIdx = (page - 1) * PER_PAGE;
    const endIdx = startIdx + PER_PAGE;
    return filteredSuggestions.slice(startIdx, endIdx);
  }, [filteredSuggestions, page]);

  const filteredTotalPages = Math.ceil(filteredSuggestions.length / PER_PAGE);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleStatusChange = (status: ProcessStatus) => {
    setSelectedStatus(status);
    setPage(1);
    onStatusChange?.(status);
  };

  const handleQueryChange = (query: string) => {
    setSearchQuery(query);
    setPage(1);
  };

  const handleArtistChange = (artists: ArtistModel[]) => {
    setSelectedArtists(artists);
    setPage(1);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedArtists([]);
    setPage(1);
  };

  const isOnFilterMode = (): boolean => {
    return searchQuery.length > 0 || selectedArtists.length > 0;
  };

  const renderSuggestionCards = () => {
    if (isLoading) {
      return (
        <Center p={8}>
          <Spinner size="xl" />
        </Center>
      );
    }

    if (!filteredSuggestions?.length) {
      return (
        <EmptyState
          header="No Data Suggestions"
          description="No Results Found"
          showButton={isOnFilterMode()}
          onButtonClick={handleClearFilters}
          buttonText="Clear Filters"
        />
      );
    }

    return (
      <Stack spacing={4}>
        {paginatedSuggestions.map(suggestion => (
          <ItemDataSuggestionCard
            key={`item_suggestion_card_${suggestion.id}`}
            suggestion={suggestion}
            onApprove={onApprove}
            onReject={onReject}
            isLoading={isActionLoading}
          />
        ))}
      </Stack>
    );
  };

  return (
    <Box width="100%">
      <Box mb={6}>
        <ItemDataSuggestionsFiltersForm
          defaultStatus={selectedStatus}
          defaultQuery={searchQuery}
          defaultArtists={selectedArtists}
          availableArtists={availableArtists}
          onStatusChange={handleStatusChange}
          onQueryChange={handleQueryChange}
          onArtistChange={handleArtistChange}
        />
      </Box>
      {renderSuggestionCards()}
      {filteredTotalPages > 1 && (
        <Box mt={8}>
          <PaginationFooter
            currentPage={page}
            lastPage={filteredTotalPages}
            siblingsCount={1}
            onPageChange={handlePageChange}
          />
        </Box>
      )}
    </Box>
  );
}
